import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import {addDays, addMonths, subMonths} from "date-fns";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import Filters from "./components/filters.jsx";
import Content from "./components/content.jsx";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => {
    const dateRef = new Date();
    const initialDate = subMonths(dateRef, 5);
    const dtStart = addMonths(initialDate, 5) < dateRef ? addDays(initialDate, 1) : initialDate;

    return {
        dtStart,
        dtEnd: dateRef,
        reason: '',
        cityFact: '',
        animationMode: false,
        onlyMyCity: false,
        archivedOccurrences: true,
    }
};

const filterLabels = {
    dtStart: "Data inicial",
    dtEnd: "Data final",
    reason: "Natureza",
    cityFact: "Município do fato",
    animationMode: "Modo animação",
    onlyMyCity: "Apenas ocorrências da minha corporação",
    archivedOccurrences: "Contabilizar ocorrências arquivadas"
};

const GeoComparison = () => {
    const [loading, setLoading] = useState(false);

    const [collapsed, setCollapsed] = useState(false);
    const buttonRef = useRef(null);
    const [fixedTop, setFixedTop] = useState(null);

    const [filters, setFilters] = useState(initialFilters());
    const [appliedFilters, setAppliedFilters] = useState(initialFilters());
    const [reportData, setReportData] = useState(null);

    const [title] = useState("Comparação georreferenciada de ocorrências");
    const [headerSelected, setHeaderSelected] = useState(-1);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : ""
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null);
            }
        });

        try {
            const periodValidation = ReportUtils.validateOnlyDates(filters.dtStart, filters.dtEnd, 5, 'months');

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            if (!ReportUtils.requiredFilterValidate(filters.reason, "Natureza", setLoading)) return;

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : null,
                reason: filters.reason.value ?? '',
                cityFact: filters.cityFact.value ?? '',
                animationMode: filters.animationMode ?? false,
                onlyMyCity: filters.onlyMyCity ?? false,
                archivedOccurrences: filters.archivedOccurrences ?? false
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd},
            filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/geo_comparison/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);
            setAppliedFilters(filters);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {

            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);

        } finally {
            setLoading(false);
        }

    };

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        setFilters(initialFilters());
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);
    const [isPrinting, setIsPrinting] = useState(false);

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className={"flex"}>

                <Template isLoading={loading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                printable={true}
                                headerChange={true}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={reportData?.length}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                                setIsPrinting={setIsPrinting}
                                setCollapsed={setCollapsed}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                        <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                              collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                            <Filters setFilters={setFilters} filters={filters}/>
                            <AutoDatetime setFilters={setFilters}/>

                        </Menu>
                    </Template.Body>
                </Template>

                <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || reportData?.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                            <NotAppliedContent reportData={reportData}/>
                            <NoDataContent reportData={reportData} data={reportData?.length}/>
                            <Content reportData={reportData} data={reportData?.length} animationMode={appliedFilters.animationMode} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} isPrinting={isPrinting}/>
                        </div>
                    </Card.Body>
                </Card>

            </div>
        </SubScroll>
    );
};

export default GeoComparison;
